import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MessageBar, MessageBarType, PrimaryButton, Spinner } from "@fluentui/react";
import { getIn, setNestedObjectValues, useFormik } from "formik";
import { t } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import TabContainer from "../../components/TabContainer/TabContainer";
import CustomTab from "../../components/TabContainer/CustomTab";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import {
  ProtocolConditionsSection,
  ProtocolDetailsSection,
  ProtocolLayoutSection,
  ProtocolPriorsSection,
  ProtocolSyncSection,
} from "../../components/HangingProtocolV2/ProtocolEditor";
import { groupFormUrl, protocolFormUrl } from "./HpV2GroupList";
import { clampTab, toGroupKind, useStepStatus } from "./hpV2FormHelpers";
import { createHpV2ProtocolSchema } from "../../Schemas/HPV2Schema";
import {
  createFieldProps,
  firstInvalidViewport,
} from "../../helpers/hangingProtocol/hpFormFields";
import userContext from "../../context/users/UserContext";
import toastContext from "../../context/toast/ToastContext";
import { ACTIONS, AuthRoutes, TOAST_TYPE, USERTYPES } from "../../constants/Constants";
import { PRIORS_UI_ENABLED } from "../../helpers/hangingProtocol/featureFlags";
import { createCategory } from "../../helpers/hangingProtocol/hpDocument";
import { GROUP_KIND } from "../../helpers/hangingProtocol/protocolEnums";
import * as hpv2 from "../../service/HPV2Service";
import "./hangingProtocolV2.css";

/**
 * One protocol — a MedDream "category" — as the platform's step wizard.
 *
 * Steps: details, the study conditions that decide when it hangs, prior and
 * comparison studies, then the layout. The layout step is the point of the
 * screen: the grid and the selected viewport's settings sit side by side and
 * neither scrolls the other away, which is what MedDream's own single-scroll
 * settings page cannot do.
 *
 * The parent group's kind decides which fields exist: categories under `hpDrag`
 * and `hpComparison` carry no prior-related keys, so that step is absent rather
 * than disabled. The priors step is also held back by `PRIORS_UI_ENABLED` until
 * the phase that ships prior authoring; a protocol's stored prior keys still
 * ride along untouched while it is hidden.
 */

const isOk = (response) => response?.status === 200 && response?.data?.result === "SUCCESS";

/** Stands in until the protocol has loaded; the steps show a spinner until then. */
const EMPTY_CATEGORY = {};

/**
 * Which step owns which top-level key of the document, so a failure can be
 * reported on the step that holds the field rather than wherever the author
 * happened to press Save. Steps not listed have nothing this schema validates.
 */
const STEP_ERROR_KEYS = {
  details: ["name", "description"],
  layout: ["panelsLayout"],
};

export default function HpV2ProtocolForm({ action = ACTIONS.EDIT }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { roles } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);

  const protocolId = searchParams.get("id");
  const groupId = searchParams.get("groupId");
  const scope = searchParams.get("scope") ?? hpv2.HP_SCOPE.USER;

  const isAdmin = roles?.[0] === USERTYPES.ADMIN;
  // Viewing is the mode the list opens into, with Edit as the way out of it. The
  // shared set belonging to an admin is a lock with no key — see the group wizard,
  // which draws the same distinction for the same reason.
  const lockedByScope = scope === hpv2.HP_SCOPE.SYSTEM && !isAdmin;
  const viewing = action === ACTIONS.VIEW;
  const readOnly = viewing || lockedByScope;
  const adding = action === ACTIONS.ADD;

  const [kind, setKind] = useState(hpv2.HP_KIND.STANDARD);
  const [groupName, setGroupName] = useState("");
  // The category Formik starts from, once the server has answered.
  const [loaded, setLoaded] = useState(null);
  // The protocol as it is stored, so Cancel can put it back — see the group
  // wizard: Cancel lands on this same screen in view mode, and the router hands
  // the new action to the component it already has rather than mounting a fresh
  // one, so nothing else would clear the edits that were just abandoned.
  const [baseline, setBaseline] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(null);
  const [updateActiveTab, setUpdateActiveTab] = useState(null);
  // The viewport the layout step should open on, set when validation fails on
  // one that is not the selected cell.
  const [focusViewport, setFocusViewport] = useState(null);

  const documentKind = toGroupKind(kind);
  const isStandard = documentKind === GROUP_KIND.STANDARD;
  const showPriors = isStandard && PRIORS_UI_ENABLED;

  /* ----------------------------------------------------------- validation */

  // Messages are baked in when the schema is built, so it is built once — as
  // v1's is. A language change remounts the screen.
  const schema = useMemo(() => createHpV2ProtocolSchema(t), []);

  /**
   * Formik holds the category so each field can carry its own error, the way the
   * rest of the platform's forms report themselves.
   */
  const formik = useFormik({
    initialValues: loaded ?? EMPTY_CATEGORY,
    enableReinitialize: true,
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    // Saving is driven from the wizard's own Save button, which has to say which
    // step and which viewport a failure is on — something Formik's submit gives
    // no hook for. See save().
    onSubmit: () => {},
  });

  const field = createFieldProps(formik);
  const draft = loaded ? formik.values : null;

  /** Whether a step has anything to report, for its status icon. */
  const stepInvalid = (key) =>
    (STEP_ERROR_KEYS[key] ?? []).some((errorKey) => Boolean(formik.errors[errorKey]));

  /**
   * Whether a cell of the layout grid is holding a value that cannot be saved.
   * Same rule as a field's own message: nothing is said until the author has
   * been in it, or has pressed Save.
   */
  const invalidViewport = (panelIndex, viewportIndex) => {
    const path = `panelsLayout.panels[${panelIndex}].layout.viewports[${viewportIndex}]`;
    if (!getIn(formik.errors, path)) return false;
    return submitted || Boolean(getIn(formik.touched, path));
  };

  /* -------------------------------------------------------------- loading */

  const load = useCallback(async () => {
    setLoading(true);

    // The group's kind decides the category's key set, so a new protocol cannot
    // be built before the group is known.
    const [summaries, stored] = await Promise.all([
      hpv2.fetchGroups(scope),
      protocolId ? hpv2.fetchProtocol(protocolId, scope) : Promise.resolve(null),
    ]);

    let groupKind = hpv2.HP_KIND.STANDARD;
    if (isOk(summaries)) {
      const summary = (summaries.data.data ?? []).find((group) => String(group.id) === String(groupId));
      groupKind = summary?.kind ?? hpv2.HP_KIND.STANDARD;
      setKind(groupKind);
      setGroupName(summary?.name ?? "");
    }

    if (stored && !isOk(stored)) {
      setError(stored?.data?.message ?? t("hanging-protocol.load-failed"));
    } else {
      const category = stored ? stored.data.data : createCategory({ kind: toGroupKind(groupKind) });
      setLoaded(category);
      setBaseline(stored ? category : null);
      setDirty(false);
      setError(null);
    }
    setLoading(false);
  }, [groupId, protocolId, scope]);

  useEffect(() => {
    load();
  }, [load]);

  /* --------------------------------------------------------------- edits */

  const onDraftChange = (next) => {
    formik.setValues(next);
    setDirty(true);
  };

  /** Drops the draft and goes, whether or not the destination remounts us. */
  const leave = (url) => {
    setDirty(false);
    setSubmitted(false);
    setFocusViewport(null);
    // Clears what was reported along with what was typed: coming back to a
    // protocol that was abandoned mid-edit must not still be red about it.
    if (baseline) formik.resetForm({ values: baseline });
    navigate(url);
  };

  const leaveTo = (url) => {
    if (dirty) {
      setConfirmLeave(url);
      return;
    }
    leave(url);
  };

  /**
   * Up to the group that owns this protocol, opened to read like anything else
   * reached by following a link rather than by pressing Edit.
   */
  const backToGroup = () =>
    leaveTo(
      groupId ? groupFormUrl(ACTIONS.VIEW, { id: groupId, scope }) : AuthRoutes.HANGINGPROTOCOLV2
    );

  /**
   * Cancel undoes the editing, not the visit: it drops the edits and shows the
   * protocol as it is stored, on the screen it was already on — Edit is the way
   * back in and the group is a breadcrumb away. A protocol being added has
   * nothing stored to fall back to, so that one still steps up to its group.
   */
  const cancel = () =>
    protocolId
      ? leaveTo(protocolFormUrl(ACTIONS.VIEW, { id: protocolId, groupId, scope }))
      : backToGroup();

  /* -------------------------------------------------------------- render */

  const { status, stepLeftHandlers } = useStepStatus({ adding, readOnly, submitted });

  const step = (children) => (
    <div className="hpv2-step">
      {loading ? <Spinner label={t("hanging-protocol.loading")} /> : <div className="hpv2-editor">{children}</div>}
    </div>
  );

  const tab = (order, { key, nameKey, subtitleKey, invalid, content }) => ({
    selectedKey: String(order),
    tabName: t(nameKey),
    tabKeyContain: (
      <CustomTab
        orderNumber={String(order)}
        tabName={t(nameKey)}
        subtitle={t(subtitleKey)}
        iconName={status(key, invalid).value}
        iconColor={status(key, invalid).color}
        action={readOnly ? ACTIONS.VIEW : action}
      />
    ),
    tabValueContain: content,
    key,
  });

  /**
   * The steps, in order. A step's number is its position in this list, so one
   * that is left out renumbers those after it instead of leaving a gap.
   */
  const steps = [
    {
      key: "details",
      nameKey: "hanging-protocol.protocol-details",
      subtitleKey: "sub-title.hanging-protocol.protocol-details",
      invalid: stepInvalid("details"),
      content: step(
        <>
          <ProtocolDetailsSection
            protocol={draft}
            disabled={readOnly}
            onChange={onDraftChange}
            field={field}
          />
          <ProtocolSyncSection protocol={draft} disabled={readOnly} onChange={onDraftChange} />
        </>
      ),
    },
    {
      key: "conditions",
      nameKey: "hanging-protocol.study-conditions",
      subtitleKey: "sub-title.hanging-protocol.study-conditions",
      content: step(<ProtocolConditionsSection protocol={draft} disabled={readOnly} onChange={onDraftChange} />),
    },
    ...(showPriors
      ? [
          {
            key: "priors",
            nameKey: "hanging-protocol.priors",
            subtitleKey: "sub-title.hanging-protocol.priors",
            content: step(<ProtocolPriorsSection protocol={draft} disabled={readOnly} onChange={onDraftChange} />),
          },
        ]
      : []),
    {
      key: "layout",
      nameKey: "hanging-protocol.layout",
      subtitleKey: "sub-title.hanging-protocol.layout",
      invalid: stepInvalid("layout"),
      content: step(
        <ProtocolLayoutSection
          protocol={draft}
          disabled={readOnly}
          showAdvanced
          onChange={onDraftChange}
          field={field}
          focus={focusViewport}
          invalidViewport={invalidViewport}
        />
      ),
    },
  ];

  const tabDetails = steps.map((item, index) => tab(index + 1, item));

  const stepHandlers = stepLeftHandlers(tabDetails.map((item) => item.key));

  /* ---------------------------------------------------------------- save */

  /**
   * Opens whatever a failed Save is about. Each field says what is wrong with it
   * itself, but only once it is on screen: the wizard shows one step at a time,
   * and the layout step one viewport at a time, so the error has to be walked to.
   *
   * Defined after `steps` because a step's number is its position in that list —
   * the same reason a hidden step renumbers the ones after it rather than
   * leaving a gap.
   */
  const showFirstError = (errors) => {
    const stepIndex = steps.findIndex((item) =>
      (STEP_ERROR_KEYS[item.key] ?? []).some((errorKey) => Boolean(errors[errorKey]))
    );
    if (stepIndex >= 0) setUpdateActiveTab(String(stepIndex + 1));

    // A fresh object each time, so pressing Save twice over the same viewport
    // still takes the author back to it.
    const viewport = firstInvalidViewport(errors);
    if (viewport) setFocusViewport({ ...viewport });
  };

  const save = async () => {
    if (saving) return;
    setSubmitted(true);

    // Pressing Save is a claim about every field, including the ones on steps
    // never opened — so mark each failing one touched and let it say so itself.
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(setNestedObjectValues(errors, true), false);
      showFirstError(errors);
      return;
    }

    setSaving(true);
    const response = await hpv2.saveProtocol(groupId, formik.values, scope);
    setSaving(false);

    if (!isOk(response)) {
      showToastv2(TOAST_TYPE.ERROR, response?.data?.message ?? t("hanging-protocol.save-failed"));
      return;
    }
    setDirty(false);
    setSubmitted(false);
    setFocusViewport(null);
    showToastv2(TOAST_TYPE.SUCCESS, t("hanging-protocol.saved"));
    // Saving finishes the editing, so it closes it — the same place Cancel lands,
    // reached by committing rather than by abandoning: the protocol as stored,
    // read on the screen it was already on, with Edit the way back in.
    if (adding) {
      // The id the server gave it is on the URL from here on, so the screen
      // reloads itself as that protocol rather than as the one being added.
      navigate(protocolFormUrl(ACTIONS.VIEW, { id: response.data.data, groupId, scope }));
    } else {
      // The reload re-reads what the server actually kept, which also moves the
      // baseline forward so the next Cancel falls back to this version rather
      // than to the one it replaced.
      load();
      navigate(protocolFormUrl(ACTIONS.VIEW, { id: protocolId, groupId, scope }));
    }
  };

  /* The way out of view mode, and the only button the header carries there. Not
     offered on the shared set to someone who could not save it anyway. */
  const topButtons = [
    {
      key: "edit",
      button: (
        <PrimaryButton
          id="edit-hpv2-protocol"
          style={{ marginLeft: "10px" }}
          text={t("button.edit")}
          onClick={() =>
            navigate(protocolFormUrl(ACTIONS.EDIT, { id: protocolId, groupId, scope }))
          }
        />
      ),
    },
  ];

  const breadCrumbItems = [
    {
      text: t("hanging-protocol.list"),
      key: "hpv2-list",
      onClick: (event) => {
        event?.preventDefault();
        leaveTo(AuthRoutes.HANGINGPROTOCOLV2);
      },
    },
    {
      text: groupName || t("hanging-protocol.group"),
      key: "hpv2-group",
      onClick: (event) => {
        event?.preventDefault();
        backToGroup();
      },
    },
    {
      text: adding ? t("hanging-protocol.new-protocol") : draft?.name || t("hanging-protocol.protocol"),
      key: "hpv2-protocol",
    },
  ];

  return (
    <>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {/* Only the lock with no key: view mode is not news, it has an Edit button. */}
      {lockedByScope && (
        <MessageBar messageBarType={MessageBarType.info}>{t("hanging-protocol.read-only")}</MessageBar>
      )}

      <TabContainer
        uniqueKey={"hpv2protocol_"}
        tabDetails={tabDetails}
        NoOfTabs={tabDetails.length}
        defaultSelectedValue={clampTab(searchParams.get("tab"), tabDetails.length)}
        previousNextButton={true}
        action={readOnly ? ACTIONS.VIEW : action}
        onCancelClick={cancel}
        onSubmitClick={save}
        topButtons={viewing && !lockedByScope ? topButtons : []}
        tabValidation={stepHandlers.tabValidation}
        onNextClick={stepHandlers.onNextClick}
        onPreviousClick={stepHandlers.onPreviousClick}
        breadCrumbItems={breadCrumbItems}
        updateActiveTab={updateActiveTab}
        setUpdateActiveTab={setUpdateActiveTab}
      />

      {confirmLeave && (
        <ConfirmModal
          showConfirmModal
          title={t("hanging-protocol.unsaved-title")}
          message={t("hanging-protocol.unsaved-body")}
          confirmButtonText={t("hanging-protocol.discard")}
          handleConfirm={() => {
            const url = confirmLeave;
            setConfirmLeave(null);
            leave(url);
          }}
          handleNotConfirm={() => setConfirmLeave(null)}
        />
      )}
    </>
  );
}
